/**
 * TMS Report — By Customer
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Users, Search, RefreshCcw, Loader2, TrendingUp } from 'lucide-react';
import { api } from '@/lib/api';

type JobRow = { id: string; customerName: string; status: string; jobType: string; arAmount: number; createdAt: string };

function normalise(r: Record<string, unknown>): JobRow {
  const d = (r.data ?? {}) as Record<string, unknown>;
  return {
    id: String(r.id ?? ''),
    customerName: String(r.customerName ?? r.customer_name ?? d.client_code ?? '—'),
    status: String(r.status ?? 'draft'),
    jobType: String(r.jobType ?? r.job_type ?? ''),
    arAmount: Number(r.arAmount ?? r.ar_amount ?? 0),
    createdAt: String(r.createdAt ?? r.created_at ?? '').slice(0, 10),
  };
}

const STATUS_ORDER = ['draft', 'confirmed', 'in_transit', 'delivered', 'completed', 'cancelled'];

export default function TmsReportCustomerPage() {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500', sort: 'created_at', order: 'desc' });
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const res = await api.get<{ items?: Record<string, unknown>[] }>(`/jobs?${params}`);
      const raw = Array.isArray(res) ? res : (res.items ?? []);
      setJobs(raw.map(normalise));
    } catch { setJobs([]); } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const filtered = useMemo(() => jobs.filter((j) => {
    if (dateFrom && j.createdAt < dateFrom) return false;
    if (dateTo && j.createdAt > dateTo) return false;
    if (search && !j.customerName.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [jobs, dateFrom, dateTo, search]);

  type CustomerStat = { name: string; total: number; ar: number; byStatus: Record<string, number>; byType: Record<string, number> };

  const rows = useMemo(() => {
    const map = new Map<string, CustomerStat>();
    for (const j of filtered) {
      const k = j.customerName;
      const s = map.get(k) ?? { name: k, total: 0, ar: 0, byStatus: {}, byType: {} };
      s.total++;
      s.ar += j.arAmount;
      s.byStatus[j.status] = (s.byStatus[j.status] ?? 0) + 1;
      s.byType[j.jobType] = (s.byType[j.jobType] ?? 0) + 1;
      map.set(k, s);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [filtered]);

  const totalJobs = rows.reduce((s, r) => s + r.total, 0);
  const totalAr = rows.reduce((s, r) => s + r.ar, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8 h-8 text-sm" placeholder="Search customer…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-[150px] text-xs" />
        <span className="text-muted-foreground text-xs">—</span>
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-[150px] text-xs" />
        <Button variant="outline" size="sm" className="h-8" onClick={fetchJobs} disabled={loading}>
          <RefreshCcw className="h-3.5 w-3.5" />
        </Button>
        <Badge variant="secondary" className="text-xs">{rows.length} customers · {totalJobs} jobs</Badge>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground text-sm">
              <Users className="h-8 w-8 mx-auto mb-3 opacity-30" />
              No jobs found for this period.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">#</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Customer</th>
                    <th className="text-right py-2 px-3 font-medium text-muted-foreground">Jobs</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Job Types</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Status Mix</th>
                    <th className="text-right py-2 px-3 font-medium text-muted-foreground">AR (SGD)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.name} className="border-t hover:bg-muted/20">
                      <td className="py-2 px-3 text-muted-foreground">{i + 1}</td>
                      <td className="py-2 px-3 font-medium">{r.name}</td>
                      <td className="py-2 px-3 text-right font-semibold">{r.total}</td>
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(r.byType).map(([t, n]) => (
                            <Badge key={t} variant="outline" className="text-[10px] px-1">{t} ×{n}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap gap-1">
                          {STATUS_ORDER.filter((s) => r.byStatus[s]).map((s) => (
                            <span key={s} className="text-[10px] text-muted-foreground">{s.replace(/_/g, ' ')} {r.byStatus[s]}</span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right font-mono">{r.ar > 0 ? r.ar.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-muted/40 border-t-2">
                  <tr>
                    <td colSpan={2} className="py-2 px-3 font-semibold text-sm">Total</td>
                    <td className="py-2 px-3 text-right font-semibold">{totalJobs}</td>
                    <td /><td />
                    <td className="py-2 px-3 text-right font-semibold font-mono">{totalAr > 0 ? totalAr.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
